import { PropsWithChildren, useEffect, useState } from 'react';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useNotifications } from 'hooks';
import { changeFavicon } from './helper';
import useAppBrand from '../useAppBrand';

// AppTitle

// ! CONTEXT LOGIC HOOK
const useAppTitleLogic = (title?: string) => {
	const { brand } = useAppBrand();
	const { t: tNotifications } = useTranslation('notifications');
	const [appDocumentTitle, setAppDocumentTitle] = useState(title ?? document.title);
	const [secondsCounter, setSeconds] = useState<number>(0);

	const {
		state: { newNotificationsCounter },
	} = useNotifications();

	useEffect(() => {
		const faviconPathPrefix = `/${brand}`;

		// alternate every second between the notification title and the default one
		if (newNotificationsCounter && secondsCounter % 2) {
			changeFavicon(`${faviconPathPrefix}-favicon-notification.ico`);

			document.title = tNotifications('browser_tab_notification_warning', { count: newNotificationsCounter });

			return;
		}

		changeFavicon(`${faviconPathPrefix}-favicon.ico`);

		document.title = appDocumentTitle;
	}, [secondsCounter, appDocumentTitle, tNotifications, newNotificationsCounter, brand]);

	useEffect(() => {
		let interval: NodeJS.Timer | undefined = undefined;
		if (newNotificationsCounter > 0) {
			interval = setInterval(() => {
				setSeconds((seconds: number) => seconds + 1);
			}, 1000);
		}
		return () => clearInterval(interval);
	}, [newNotificationsCounter]);

	return { document_title: appDocumentTitle, setAppDocumentTitle };
};

// ! CONTEXT
type IAppTitleContextData = ReturnType<typeof useAppTitleLogic>;
const AppTitleContext = createContext<IAppTitleContextData | undefined>(undefined);

// ! CONTEXT PROVIDER
export const AppTitleProvider = ({ children = <Outlet /> }: PropsWithChildren) => {
	const value = useAppTitleLogic();

	return <AppTitleContext.Provider value={value}>{children}</AppTitleContext.Provider>;
};

// ! CONTEXT CONSUMER HOOK
export const useAppTitle = () => {
	const ctx = useContext(AppTitleContext);

	if (!ctx) {
		throw new Error('useAppTitle must be used inside AppTitleProvider');
	}

	return ctx;
};
